import { useState, useEffect, useRef } from "react";

import { DEFAULT_ITEMS_PER_PAGE } from "../constants";
import { FiltersProps, FilterTerms } from "../filters/interfaces";
import { normalizeTextArrayFilterValue } from "../filters/normalizeTextArrayFilterValue";
import { ListDataFiltersTypeEnum } from "../interfaces/enums";

import { useDebounce } from "@/hooks/index";
import { useURLFilter } from "@/hooks/useUrlFilter";
import { extractFilterQueries } from "@/utils/ExtractFilterQueries";
import { filterTruthyObjectProperties } from "@/utils/FilterTruthyObjectProperties";

interface BulkOptionsFilters {
  [key: string]: string | boolean;
}

const DEBOUNCE_TIMER_MS = 500;

const SELECTED_KEYS_RANGE_TYPES: ReadonlySet<ListDataFiltersTypeEnum> = new Set(
  [
    ListDataFiltersTypeEnum.CURRENT_ITEMS_ONLY,
    ListDataFiltersTypeEnum.DATE,
    ListDataFiltersTypeEnum.DATE_RANGE,
    ListDataFiltersTypeEnum.SELECT
  ]
);

function normalizeVisibleTermsFromUrl(
  termsFromUrl: Record<string, string>,
  filters: FiltersProps[]
): Record<string, string> {
  const normalizedTerms = { ...termsFromUrl };

  for (const filter of filters) {
    if (!filter.normalizeValueFromUrl) continue;

    const filterQueries = Array.isArray(filter.filterQuery)
      ? filter.filterQuery
      : [filter.filterQuery];

    for (const filterQuery of filterQueries) {
      if (!filterQuery || termsFromUrl[filterQuery] === undefined) continue;

      normalizedTerms[filterQuery] = filter.normalizeValueFromUrl(
        termsFromUrl[filterQuery]
      );
    }
  }

  return normalizedTerms;
}

/*
 * Todo:
 * Esse arquivo é uma extração de hooks relacionados a filtros de listas do antigo arquivo de ListData.
 * Ele foi separado para melhorar a organização do código e facilitar a manutenção,
 * mas ainda não está refatorado, e alguns de seus nomes são pouco semânticos.
 *
 * Melhorar em um PR futuro.
 */
export function useListFilters(filters?: FiltersProps[]) {
  const { urlParams } = useURLFilter();
  const hasHydratedFiltersFromUrl = useRef(false);

  const [selectedKeys, setSelectedKeys] = useState<Record<string, string>>({});
  const [selectedTerms, setSelectedTerms] = useState<FilterTerms>({});
  const [visibleTerms, setVisibleTerms] = useState<Record<string, string>>({});
  const [bulkOptionsFilters, setBulkOptionsFilters] =
    useState<BulkOptionsFilters>({});

  const defaultOptionsFilters = {
    limit: urlParams?.params?.limit || String(DEFAULT_ITEMS_PER_PAGE)
  };

  useEffect(() => {
    if (hasHydratedFiltersFromUrl.current || !urlParams?.params) {
      return;
    }

    const filtersWithOptions = filters?.filter(
      filter =>
        filter.type === ListDataFiltersTypeEnum.SELECT &&
        Array.isArray(filter.options) &&
        filter.options.length
    );

    const allOptionsLoaded = filtersWithOptions && filtersWithOptions.length;

    if (!allOptionsLoaded) {
      return;
    }

    const selectedKeysEnabledToFilter = extractFilterQueries(filters, f =>
      SELECTED_KEYS_RANGE_TYPES.has(f.type)
    );

    const selectedTermsEnabledToFilter = extractFilterQueries(
      filters,
      filter => !SELECTED_KEYS_RANGE_TYPES.has(filter.type)
    );

    const keysFromUrl = filterTruthyObjectProperties(
      urlParams.params,
      selectedKeysEnabledToFilter
    );

    const termsFromUrl = filterTruthyObjectProperties(
      urlParams.params,
      selectedTermsEnabledToFilter
    );

    const selectedTermsFromUrl: FilterTerms = { ...termsFromUrl };
    const textArrayFilters = filters.filter(
      filter => filter.type === ListDataFiltersTypeEnum.ARRAY_TEXT
    );

    for (const filter of textArrayFilters) {
      const key = filter.filterQuery as string;
      const values = normalizeTextArrayFilterValue(
        urlParams.params[key],
        filter.maskFormatter
      );

      if (values.length) {
        selectedTermsFromUrl[key] = filter.sendArrayAsCommaSeparatedString
          ? values.join(",")
          : values;
      } else {
        delete selectedTermsFromUrl[key];
      }
    }

    if (Object.keys(keysFromUrl).length > 0) {
      setSelectedKeys(keysFromUrl);
    }

    if (Object.keys(selectedTermsFromUrl).length > 0) {
      setSelectedTerms(selectedTermsFromUrl);
      setVisibleTerms(normalizeVisibleTermsFromUrl(termsFromUrl, filters));
    }

    hasHydratedFiltersFromUrl.current = true;
  }, [urlParams?.params, filters]);

  const debouncedTerms = useDebounce(selectedTerms, DEBOUNCE_TIMER_MS);

  return {
    selectedKeysRangeTypes: SELECTED_KEYS_RANGE_TYPES,
    selectedKeys,
    setSelectedKeys,
    selectedTerms,
    setSelectedTerms,
    visibleTerms,
    setVisibleTerms,
    bulkOptionsFilters,
    setBulkOptionsFilters,
    debouncedTerms,
    defaultOptionsFilters
  };
}
